import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { History, ChevronRight } from 'lucide-react'
import { Spinner } from 'react-bootstrap'
import { CompanyPageAccordion } from 'components/NewCompanyPage/CompanyPageAccordion'
import { groupEventsByDate } from 'components/NewCompanyPage/EventHistory/utils'
import apiService from 'services/api'
import { MobileEventItem } from './MobileEventItem'

interface MobileEventHistoryAccordionProps {
  chainProxyId: number
  doorProxyId?: number
}

export function MobileEventHistoryAccordion({
  chainProxyId,
  doorProxyId,
}: MobileEventHistoryAccordionProps) {
  const api = apiService()
  const navigate = useNavigate()

  const handleViewAllClick = () => {
    // Navigate to the events page with contact_company_id filter
    const params = new URLSearchParams()
    params.set('contact_company_id', String(chainProxyId))
    if (doorProxyId) {
      params.set('door_proxy_id', String(doorProxyId))
    }
    navigate(`/events?${params.toString()}`)
  }

  // Build query params for fetching all events
  const buildQueryParams = (pageParam: number) => {
    const baseParams: Record<string, unknown> = {
      page: pageParam,
      page_size: 20,
    }

    if (doorProxyId) {
      baseParams.door_proxy_id = doorProxyId
    }

    return baseParams
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['mobileCompanyActivity', chainProxyId, doorProxyId],
    queryFn: ({ pageParam }) =>
      api.getCompanyActivityList(chainProxyId, buildQueryParams(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      if (totalFetched < lastPage.count) {
        return allPages.length + 1
      }
      return undefined
    },
    enabled: !!chainProxyId,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Flatten all pages of events into a single array and group by date
  const groupedEvents = useMemo(() => {
    if (!data?.pages) return []
    const allEvents = data.pages.flatMap((page) => page.results)

    // Filter out unwanted events
    const filteredEvents = allEvents.filter((event) => {
      // Filter out document events with empty field_history_objects
      if (event.content_type?.model === 'document') {
        return (
          event.field_history_objects && event.field_history_objects.length > 0
        )
      }
      // Filter out email UPDATE events - we only want to show email CREATE events
      if (
        event.content_type?.model === 'emailmessage' &&
        event.action_type === 'UPDATE'
      ) {
        return false
      }
      return true
    })

    return groupEventsByDate(filteredEvents)
  }, [data?.pages])

  // Custom header with History icon and "View All" link
  const headerLeft = (
    <div className="flex items-center gap-2">
      <History className="w-4 h-4 text-gray-500" />
      <span className="font-semibold text-sm">HISTORY</span>
      <span className="text-gray-500 text-sm">(Past Events)</span>
    </div>
  )

  const headerRight = (
    <div
      onClick={(e) => {
        e.stopPropagation()
        handleViewAllClick()
      }}
      className="flex items-center gap-1 text-fb-dark-green text-sm cursor-pointer hover:underline"
    >
      <span>View All</span>
      <ChevronRight className="w-4 h-4" />
    </div>
  )

  return (
    <CompanyPageAccordion
      title=""
      hideChevron={true}
      customTitle={headerLeft}
      headerRight={headerRight}
      defaultOpen={true}
      stickyHeader={false}
    >
      <div className="relative min-h-[100px] max-h-[400px] overflow-y-auto">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {/* Error state */}
        {!!error && (
          <div className="py-8 text-center text-sm text-red-500">
            Failed to load event history
          </div>
        )}

        {/* Empty state */}
        {!error && groupedEvents.length === 0 && !isLoading && (
          <div className="py-8 text-center text-sm text-gray-500">
            No recent events found
          </div>
        )}

        {/* Content */}
        {!error && groupedEvents.length > 0 && (
          <div className="space-y-4">
            {groupedEvents.map((group) => (
              <div key={group.date} className="space-y-2">
                {/* Date header */}
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-1">
                  {group.relativeDate.toUpperCase()}
                </div>

                {/* Events for this date */}
                <div className="space-y-1">
                  {group.events.map((event, idx) => (
                    <MobileEventItem
                      key={`${event.created_at}-${idx}`}
                      event={event}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex justify-center pt-2 pb-4">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-4 py-1.5 text-xs bg-fb-dark-green text-white rounded hover:bg-fb-dark-green-hover transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </CompanyPageAccordion>
  )
}
